import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Shield, UserPlus, Check, Loader2, ArrowLeft, AlertCircle, Crown, Mic, Video, Music, Palette, Radio, Users } from 'lucide-react';
import { API_BASE_URL as API } from '../config/api';

// Creator types/roles available
const CREATOR_TYPES = [
  { value: 'artist', label: 'Artist', icon: Music, description: 'Music artist, rapper, singer' },
  { value: 'listener', label: 'Listener', icon: Users, description: 'Fan/listener account' },
  { value: 'podcaster', label: 'Podcaster', icon: Mic, description: 'Podcast host' },
  { value: 'streamer', label: 'Streamer', icon: Radio, description: 'Live streamer' },
  { value: 'videographer', label: 'Videographer', icon: Video, description: 'Video content creator' },
  { value: 'studio', label: 'Music Studio/Engineer', icon: Music, description: 'Recording studio or engineer' },
  { value: 'graphic_designer', label: 'Graphic Designer', icon: Palette, description: 'Visual artist, designer' },
  { value: 'producer', label: 'Producer', icon: Music, description: 'Beat maker, music producer' },
];

// Subscription tiers available
const TIERS = [
  { value: 'free', label: 'Free', description: 'Basic features, limited uploads' },
  { value: 'starter', label: 'Basic/Starter', description: 'Essential features' },
  { value: 'growth', label: 'Growth', description: 'Growing creator features' },
  { value: 'pro', label: 'Pro', description: 'Professional features' },
  { value: 'elite', label: 'Elite', description: 'Elite creator features' },
  { value: 'executive', label: 'Executive', description: 'Executive tier features' },
  { value: 'enterprise', label: 'Enterprise/Label', description: 'Full enterprise features' },
];

// Optional add-ons
const ADDONS = [
  { key: 'is_verified', label: 'Verification Badge', description: 'Blue checkmark on profile' },
  { key: 'can_upload_direct', label: 'Direct Uploads', description: 'Upload without review' },
  { key: 'can_go_live', label: 'Go Live', description: 'Live streaming capability' },
  { key: 'featured_eligible', label: 'Featured Eligible', description: 'Can be featured on homepage' },
  { key: 'boost_credits', label: 'Boost Credits (10)', description: '10 promotion boost credits' },
];

export default function AdminGrantTierPage() {
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('artist');
  const [selectedTier, setSelectedTier] = useState('enterprise');
  const [selectedAddons, setSelectedAddons] = useState({
    is_verified: true,
    can_upload_direct: true,
    can_go_live: true,
    featured_eligible: true,
    boost_credits: false,
  });
  const [durationDays, setDurationDays] = useState(365);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const isOwner = user?.role === 'owner' || user?.is_owner;

  const toggleAddon = (key) => {
    setSelectedAddons(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleGrantTier = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    if (!trimmedEmail.includes('@') || !trimmedEmail.includes('.')) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Show preview first
    if (!showPreview) {
      setShowPreview(true);
      return;
    }

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      let authHeaders = getAuthHeaders();
      
      // Fallback: try localStorage if no token in context
      if (!authHeaders.Authorization) {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          authHeaders = { Authorization: `Bearer ${storedToken}` };
          console.log('[AdminGrantTier] Using token from localStorage');
        }
      }
      
      if (!authHeaders.Authorization) {
        toast.error('Authentication required - please log in again');
        setError({ code: 'AUTH_ERROR', message: 'No authentication token found. Please log in again.' });
        setLoading(false);
        return;
      }

      console.log('[AdminGrantTier] Sending request for:', trimmedEmail, 'role:', selectedRole, 'tier:', selectedTier);
      console.log('[AdminGrantTier] API URL:', `${API}/api/admin/control-center/grant-tier`);
      
      const res = await fetch(`${API}/api/admin/control-center/grant-tier`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          user_email: trimmedEmail,
          tier: selectedTier,
          role: selectedRole,
          addons: selectedAddons,
          duration_days: durationDays,
        })
      });

      console.log('[AdminGrantTier] Response status:', res.status);

      let data;
      const contentType = res.headers.get('content-type');
      
      // First check for non-OK status codes
      if (!res.ok) {
        let errorDetail = `HTTP ${res.status}`;
        try {
          const errorBody = await res.text();
          console.error('[AdminGrantTier] Error response body:', errorBody.substring(0, 500));
          
          // Try to parse as JSON
          if (errorBody.startsWith('{')) {
            const errorJson = JSON.parse(errorBody);
            errorDetail = errorJson.detail || errorJson.message || errorJson.error || errorDetail;
          } else if (errorBody.includes('<!DOCTYPE') || errorBody.includes('<html')) {
            // It's HTML - likely a gateway error
            errorDetail = `Gateway error (${res.status}). Please try again in a moment.`;
          } else {
            errorDetail = errorBody.substring(0, 200) || errorDetail;
          }
        } catch (e) {
          console.error('[AdminGrantTier] Could not parse error:', e);
        }
        
        setError({ 
          code: res.status === 404 ? 'USER_NOT_FOUND' : 
                res.status === 403 ? 'FORBIDDEN' : 
                res.status === 401 ? 'UNAUTHORIZED' : 
                res.status === 502 ? 'GATEWAY_ERROR' :
                res.status === 503 ? 'SERVICE_UNAVAILABLE' :
                res.status === 400 ? 'VALIDATION_ERROR' : 'SERVER_ERROR',
          message: errorDetail,
          status: res.status
        });
        
        toast.error(errorDetail);
        setLoading(false);
        return;
      }
      
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await res.json();
        } catch (parseError) {
          console.error('[AdminGrantTier] JSON parse error:', parseError);
          setError({ code: 'PARSE_ERROR', message: 'Invalid response format from server' });
          toast.error('Invalid response format from server');
          setLoading(false);
          return;
        }
      } else {
        const text = await res.text();
        console.error('[AdminGrantTier] Non-JSON response:', text.substring(0, 200));
        setError({ code: 'INVALID_RESPONSE', message: 'Server returned an unexpected response format' });
        toast.error('Server returned an unexpected response format');
        setLoading(false);
        return;
      }

      console.log('[AdminGrantTier] Response data:', data);

      if (res.ok && data.success) {
        setResult(data);
        const roleLabel = CREATOR_TYPES.find(r => r.value === selectedRole)?.label || selectedRole;
        const tierLabel = TIERS.find(t => t.value === selectedTier)?.label || selectedTier;
        toast.success(`${tierLabel} ${roleLabel} access granted to ${trimmedEmail}!`);
        setEmail('');
        setShowPreview(false);
      } else {
        const errorMessage = data.detail || data.error || data.message || 'Failed to grant access';
        let displayError = errorMessage;
        if (typeof errorMessage === 'object') {
          displayError = errorMessage.message || errorMessage.code || JSON.stringify(errorMessage);
        }
        
        setError({ 
          code: res.status === 404 ? 'USER_NOT_FOUND' : 
                res.status === 403 ? 'FORBIDDEN' : 
                res.status === 401 ? 'UNAUTHORIZED' : 
                res.status === 400 ? 'VALIDATION_ERROR' : 'SERVER_ERROR',
          message: displayError,
          status: res.status
        });
        
        if (res.status === 404) {
          toast.error(`User not found: ${trimmedEmail} - will create new account`);
        } else if (res.status === 403) {
          toast.error('Access denied - admin privileges required');
        } else if (res.status === 401) {
          toast.error('Session expired - please log in again');
        } else {
          toast.error(displayError);
        }
      }
    } catch (err) {
      console.error('[AdminGrantTier] Error:', err);
      const isNetworkError = err.name === 'TypeError' && err.message.includes('fetch');
      
      setError({
        code: isNetworkError ? 'NETWORK_ERROR' : 'REQUEST_FAILED',
        message: err.message || 'Request failed'
      });
      
      if (isNetworkError) {
        toast.error('Network error - check your connection and try again');
      } else {
        toast.error(err.message || 'Request failed - please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  // Not authenticated
  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="p-6 text-center max-w-md">
          <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-xl font-bold mb-2">Admin Access Required</h1>
          <p className="text-muted-foreground mb-4">Please log in as platform owner</p>
          <Button onClick={() => navigate('/login')}>Log In</Button>
        </Card>
      </div>
    );
  }

  // Not owner
  if (!isOwner) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="p-6 text-center max-w-md">
          <Shield className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h1 className="text-xl font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-4">Only platform owners can access this page</p>
          <Button variant="outline" onClick={() => navigate(-1)}>Go Back</Button>
        </Card>
      </div>
    );
  }

  const selectedRoleInfo = CREATOR_TYPES.find(r => r.value === selectedRole);
  const selectedTierInfo = TIERS.find(t => t.value === selectedTier);

  return (
    <div className="min-h-screen overflow-y-auto" style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}>
      <div className="p-4 py-6 sm:py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold truncate">Admin: Grant Creator Access</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Grant any role and tier to any user</p>
            </div>
          </div>

          <Card className="p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-bold">Grant Creator Access</h2>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">Logged in as: {user?.email}</p>
              </div>
            </div>

            <div className="space-y-5 sm:space-y-6">
              {/* Email Input */}
              <div>
                <Label htmlFor="email" className="text-sm">User Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setShowPreview(false); }}
                  placeholder="user@example.com"
                  className="mt-1"
                  disabled={loading}
                  data-testid="grant-tier-email"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  If user doesn't exist, a new account will be created
                </p>
              </div>

              {/* Creator Type Selection */}
              <div>
                <Label className="text-sm">Creator Type / Role *</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-2 mt-2">
                  {CREATOR_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => { setSelectedRole(type.value); setShowPreview(false); }}
                        className={`p-2 sm:p-3 rounded-lg border text-left transition-all ${
                          selectedRole === type.value 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-border'
                        }`}
                        data-testid={`role-${type.value}`}
                      >
                        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 mb-0.5 sm:mb-1 ${selectedRole === type.value ? 'text-primary' : 'text-muted-foreground'}`} />
                        <p className="text-xs sm:text-sm font-medium leading-tight">{type.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tier Selection */}
              <div>
                <Label className="text-sm">Subscription Tier *</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-2 mt-2">
                  {TIERS.map(tier => (
                    <button
                      key={tier.value}
                      onClick={() => { setSelectedTier(tier.value); setShowPreview(false); }}
                      className={`p-2 sm:p-3 rounded-lg border text-left transition-all ${
                        selectedTier === tier.value 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-border'
                      }`}
                      data-testid={`tier-${tier.value}`}
                    >
                      <p className="text-xs sm:text-sm font-medium">{tier.label}</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">{tier.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Add-ons */}
              <div>
                <Label className="text-sm">Optional Add-ons</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 mt-2">
                  {ADDONS.map(addon => (
                  <button
                    key={addon.key}
                    onClick={() => { toggleAddon(addon.key); setShowPreview(false); }}
                    className={`p-2.5 sm:p-3 rounded-lg border text-left transition-all flex items-center gap-2.5 sm:gap-3 ${
                      selectedAddons[addon.key] 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-border hover:border-border'
                    }`}
                    data-testid={`addon-${addon.key}`}
                  >
                    <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                      selectedAddons[addon.key] ? 'bg-green-500 border-green-500' : 'border-border'
                    }`}>
                      {selectedAddons[addon.key] && <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-medium">{addon.label}</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground leading-tight">{addon.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div>
              <Label htmlFor="duration" className="text-sm">Duration (days)</Label>
              <Input
                id="duration"
                type="number"
                value={durationDays}
                onChange={(e) => { setDurationDays(parseInt(e.target.value) || 365); setShowPreview(false); }}
                min={1}
                max={3650}
                className="mt-1 w-28 sm:w-32"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Set to 0 for permanent (no expiration)
              </p>
            </div>

            {/* Preview Summary */}
            {showPreview && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 sm:p-4">
                <p className="text-sm sm:text-base font-medium text-amber-500 mb-2 sm:mb-3">⚠️ Confirm Grant Details</p>
                <div className="text-xs sm:text-sm space-y-1 sm:space-y-1.5 bg-black/20 rounded p-2.5 sm:p-3">
                  <p><span className="text-muted-foreground">Email:</span> <strong className="break-all">{email}</strong></p>
                  <p><span className="text-muted-foreground">Role:</span> <strong>{selectedRoleInfo?.label}</strong></p>
                  <p><span className="text-muted-foreground">Tier:</span> <strong>{selectedTierInfo?.label}</strong></p>
                  <p><span className="text-muted-foreground">Duration:</span> <strong>{durationDays > 0 ? `${durationDays} days` : 'Permanent'}</strong></p>
                  <p><span className="text-muted-foreground">Billing:</span> <strong>Bypassed (Free)</strong></p>
                  <div className="pt-2 border-t border-border mt-2">
                    <p className="text-muted-foreground mb-1">Features enabled:</p>
                    <ul className="text-[10px] sm:text-xs space-y-0.5">
                      {selectedAddons.is_verified && <li>• Verification Badge</li>}
                      {selectedAddons.can_upload_direct && <li>• Direct Uploads</li>}
                      {selectedAddons.can_go_live && <li>• Go Live Streaming</li>}
                      {selectedAddons.featured_eligible && <li>• Featured Eligible</li>}
                      {selectedAddons.boost_credits && <li>• 10 Boost Credits</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Action Button - Mobile optimized with safe area */}
            <div className="pt-2 sm:pt-0">
              <Button 
                onClick={handleGrantTier} 
                disabled={loading || !email.trim()}
                className="w-full min-h-[48px] sm:min-h-[44px] text-sm sm:text-base relative z-50"
                size="lg"
                data-testid="grant-tier-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Granting Access...
                  </>
                ) : showPreview ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Confirm & Grant Access
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Preview Grant
                  </>
                )}
              </Button>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 sm:p-4">
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm sm:text-base font-medium text-red-500">
                      {error.code === 'USER_NOT_FOUND' ? 'User Not Found (will create)' :
                       error.code === 'FORBIDDEN' ? 'Access Denied' :
                       error.code === 'UNAUTHORIZED' ? 'Session Expired' :
                       error.code === 'NETWORK_ERROR' ? 'Network Error' :
                       'Error'}
                    </p>
                    <p className="text-xs sm:text-sm text-red-400 mt-1 break-all">{error.message}</p>
                    {error.status && (
                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Status: {error.status}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Success Display */}
            {result && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 sm:p-4">
                <p className="text-sm sm:text-base font-medium text-green-500 mb-2 sm:mb-3">✓ Access Granted Successfully!</p>
                <div className="text-xs sm:text-sm space-y-1 sm:space-y-1.5 bg-black/20 rounded p-2.5 sm:p-3">
                  <p><span className="text-muted-foreground">Action:</span> {result.action}</p>
                  <p><span className="text-muted-foreground">Email:</span> <span className="break-all">{result.user?.email}</span></p>
                  <p><span className="text-muted-foreground">User ID:</span> <span className="break-all">{result.user?.user_id}</span></p>
                  <p><span className="text-muted-foreground">Role:</span> {result.user?.role}</p>
                  <p><span className="text-muted-foreground">Tier:</span> {result.user?.tier}</p>
                  <p><span className="text-muted-foreground">Subscription:</span> {result.user?.subscription_active ? 'Active' : 'Inactive'}</p>
                  <p><span className="text-muted-foreground">Enterprise Features:</span> {result.user?.enterprise_features_enabled ? 'Enabled' : 'Disabled'}</p>
                </div>
              </div>
            )}
          </div>
        </Card>
        </div>
      </div>
    </div>
  );
}
